import { createBrowserRouter, Navigate } from 'react-router-dom';
import { DefaultRedirect } from './components/DefaultRedirect';
import { Layout } from './Layout';
import { LoginPage } from './pages/auth/LoginPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { ChangePasswordRequiredPage } from './pages/auth/ChangePasswordRequiredPage';
import { LogoutPage } from './pages/auth/LogoutPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthInitializer } from './components/AuthInitializer';
import { SocietiesListPage } from './features/societies/pages/SocietiesListPage';


import { ExternalUsersListPage } from './features/users/external-users/ExternalUsersListPage';
import { ExternalUserDetailPage } from './features/users/external-users/ExternalUserDetailPage';
import { ExternalUserCreatePage } from './features/users/external-users/pages/ExternalUserCreatePage';
import { ExternalUserEditPage } from './features/users/external-users/pages/ExternalUserEditPage';
import { WarrantiesListPage } from './features/warranties/pages/WarrantiesListPage';
import { WarrantyDetailPage } from './features/warranties/pages/WarrantyDetailPage';
import { WarrantyCreatePage } from './features/warranties/pages/WarrantyCreatePage';
import { ContractsListPage } from './features/contracts/pages/ContractsListPage';
import { ContractDetailPage } from './features/contracts/pages/ContractDetailPage';
import { ContractCreatePage } from './features/contracts/pages/ContractCreatePage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ForbiddenPage } from './pages/error/ForbiddenPage';
import { NotFoundPage } from './pages/error/NotFoundPage';
import { DeliveryProofList, DeliveryProofNew } from './features/delivery-proof/pages';
import { SavListPage } from './features/sav/pages/SavListPage';
import { SavDetailPage } from './features/sav/pages/SavDetailPage';
import RepairerInvoicesPage from './features/sav/pages/RepairerInvoicesPage';
import { InvoiceListPage } from './features/invoices/pages/InvoiceListPage';
import { StripeAdminPage } from './features/stripe-admin/pages/StripeAdminPage';
import { PaymentPage } from './features/payment/pages/PaymentPage';
import { PlvPage } from './features/plv/pages/PlvPage';
import { LegalLibraryPage } from './features/legal-library/pages/LegalLibraryPage';
import PresaleSheetPage from '@/features/presale-sheet/pages/PresaleSheetPage';
import { NewsFeedPage } from '@/features/news/components/reader/NewsFeedPage';
import { NewsDetailPage } from '@/features/news/components/reader/NewsDetailPage';

export const outilProRouter = createBrowserRouter([
  // Ancienne passerelle d'auto-login depuis l'outil pro v1 (Symfony, retiré).
  // Conservée en redirection pour que les anciens liens ne tombent pas en 404.
  {
    path: '/auth/token-login',
    element: <Navigate to="/login" replace />,
  },
  // Déconnexion : invalide la session (API + locale) puis renvoie vers /login.
  {
    path: '/auth/logout',
    element: <LogoutPage />,
  },
  // Portail public réparateur (cycle paiement), accès par code court 6 chars
  // depuis le BPAR — XXXXXX ou XXXX-XX, l'API normalise les deux.
  // Volontairement hors AuthInitializer ET hors Layout : le réparateur n'est
  // pas un utilisateur de l'outil pro, il ne doit ni déclencher un refresh de
  // session ni voir le chrome de l'application. La page porte son propre
  // layout pleine page.
  // Cf SPECS/repairer_payment/README.md Phase 2.
  {
    path: '/r/:code',
    lazy: () =>
      import('./RepairerInvoicePortal').then((m) => ({ Component: m.default })),
  },
  {
    path: '/',
    element: (
      <AuthInitializer>
        <Layout />
      </AuthInitializer>
    ),
    children: [
      // Redirect root to dashboard or contracts based on permissions
      {
        index: true,
        element: <DefaultRedirect />,
      },
      // Public routes
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'change-password-required',
        element: <ChangePasswordRequiredPage />,
      },
      // Public payment page (accessed via secure token link)
      {
        path: 'payment/:token',
        element: <PaymentPage />,
      },
      // Error pages
      {
        path: '403',
        element: <ForbiddenPage />,
      },
      {
        path: '404',
        element: <NotFoundPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
      // Protected routes
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      // Profile
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      // Societies routes
      {
        path: 'societies',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SocietiesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                lazy: () => import("./features/societies/pages/SocietyTabRedirect").then(m => ({ Component: m.default })),
              },
              {
                path: ':tab',
                lazy: () => import("./features/societies/pages/SocietyDetailPage").then(m => ({ Component: m.SocietyDetailPage })),
              },
            ],
          },
        ],
      },
      // Users routes - External
      {
        path: 'users-externes',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission="external_user.list">
                <ExternalUsersListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute requiredPermission="external_user.create">
                <ExternalUserCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute requiredPermission="external_user.list">
                <ExternalUserDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute requiredPermission="external_user.edit">
                <ExternalUserEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Warranties routes
      {
        path: 'warranties',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <WarrantiesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute requiredPermission="warranty.create">
                <WarrantyCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <WarrantyDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Contracts routes
      {
        path: 'contracts',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ContractsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute requiredPermission="contract.create" requiredSocietyOption="canCreateContract">
                <ContractCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ContractDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // SAV routes
      {
        path: 'sav',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SavListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <SavDetailPage embedded />
              </ProtectedRoute>
            ),
          },
          {
            // Cycle paiement réparateur — liste des factures à régler
            // (cf SPECS/repairer_payment Phase 4.2)
            path: 'repairer-invoices',
            element: (
              <ProtectedRoute>
                <RepairerInvoicesPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Invoices routes
      {
        path: 'invoices',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission="invoice.list">
                <InvoiceListPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Administration Stripe (lecture seule) — réservé aux propriétaires de compte.
      // La barrière réelle est serveur (viewableAccounts) : la permission ne suffit pas.
      {
        path: 'online-payments',
        element: (
          <ProtectedRoute requiredPermission="stripe.self">
            <StripeAdminPage />
          </ProtectedRoute>
        ),
      },
      // Ancienne URL, conservée en redirection pour les favoris et les returnUrl.
      {
        path: 'stripe',
        element: <Navigate to="/online-payments" replace />,
      },
      // Brochure avant vente (gated par l'option société presaleSheet)
      {
        path: 'presale-sheet',
        element: (
          <ProtectedRoute requiredSocietyOption="presaleSheet">
            <PresaleSheetPage />
          </ProtectedRoute>
        ),
      },
      // PLV routes
      {
        path: 'plv',
        element: (
          <ProtectedRoute requiredPermission="plv.list">
            <PlvPage />
          </ProtectedRoute>
        ),
      },
      // Nouveautés (fil d'annonces produit) : accessibles à tout utilisateur
      // authentifié. `/nouveautes/<slug>` est l'URL stable communiquée par les
      // rédacteurs (cf. NEWS_PUBLIC_PATH_PREFIX).
      {
        path: 'nouveautes',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <NewsFeedPage platform="PRO" />
              </ProtectedRoute>
            ),
          },
          {
            path: ':slug',
            element: (
              <ProtectedRoute>
                <NewsDetailPage platform="PRO" />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Legal Library (accessible à tout utilisateur authentifié)
      {
        path: 'legal-library',
        element: <LegalLibraryPage />,
      },
      // Delivery Proofs routes (gated par l'option société deliveryProofCreate)
      {
        path: 'delivery-proofs',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredSocietyOption="deliveryProofCreate">
                <DeliveryProofList />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute requiredPermission="delivery_proof.create" requiredSocietyOption="deliveryProofCreate">
                <DeliveryProofNew />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);

export default outilProRouter;


